import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
  Tooltip,
} from '@mui/material';
import AionaLogo from '../../assets/aiona/AIONA_白.png';
import { styled } from '@mui/material/styles';
import {
  Dashboard as DashboardIcon,
  Description as DocumentsIcon,
  Search as SearchIcon,
  Upload as UploadIcon,
  Assessment as DRBFMIcon,
  Checklist as VerificationIcon,
  ViewInAr as CADViewerIcon,
  Settings as SettingsIcon,
  Help as HelpIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { LAYOUT, Z_INDEX } from '../../constants/layout';
import { selectCadEnabled } from '@features/featureFlags/featureFlagsSlice';
import { SidebarMode } from '@features/ui/uiSlice';
import { useAppSelector } from '@store/index';

interface SidebarProps {
  sidebarMode: SidebarMode;
  isMobile: boolean;
  onCloseMobile?: () => void;
}

const EXPANDED_WIDTH = LAYOUT.SIDEBAR_WIDTH_EXPANDED;
const MINI_WIDTH = LAYOUT.SIDEBAR_WIDTH_MINI;

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: 0,
  ...theme.mixins.toolbar,
  justifyContent: 'center',
}));

const Sidebar: React.FC<SidebarProps> = ({ sidebarMode, isMobile, onCloseMobile }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const cadEnabled = useAppSelector(selectCadEnabled);
  const [hovered, setHovered] = useState(false);

  const isMini = sidebarMode === 'mini';
  const isExpanded = sidebarMode === 'expanded' || (isMini && hovered);
  const currentWidth = isExpanded ? EXPANDED_WIDTH : MINI_WIDTH;

  const navigationItems = [
    { title: t('navigation.dashboard'), path: '/dashboard', icon: <DashboardIcon /> },
    { title: t('navigation.documents'), path: '/documents', icon: <DocumentsIcon /> },
    { title: t('navigation.search'), path: '/search', icon: <SearchIcon /> },
  ];

  const actionItems = [
    { title: t('navigation.uploadDocument'), path: '/documents/upload', icon: <UploadIcon /> },
    { title: t('navigation.drbfm'), path: '/drbfm', icon: <DRBFMIcon /> },
    { title: t('navigation.verification'), path: '/verification', icon: <VerificationIcon /> },
    // Only show CAD viewer when feature is enabled (or still loading)
    ...(cadEnabled !== false
      ? [{ title: t('navigation.cadViewer'), path: '/cad-viewer', icon: <CADViewerIcon /> }]
      : []),
  ];

  const bottomItems = [
    { title: t('navigation.settings'), path: '/settings', icon: <SettingsIcon /> },
    { title: t('navigation.help'), path: '/help', icon: <HelpIcon /> },
  ];

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile) onCloseMobile?.();
  };

  const isActive = (path: string) => location.pathname === path;

  const renderNavItem = (item: { title: string; path: string; icon: React.ReactElement }) => (
    <ListItem key={item.path} disablePadding sx={{ display: 'block' }}>
      <Tooltip title={!isExpanded ? item.title : ''} placement="right" arrow>
        <ListItemButton
          selected={isActive(item.path)}
          onClick={() => handleNavigation(item.path)}
          sx={{
            minHeight: 48,
            justifyContent: isExpanded ? 'initial' : 'center',
            px: 2.5,
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: 0,
              mr: isExpanded ? 2 : 'auto',
              justifyContent: 'center',
            }}
          >
            {item.icon}
          </ListItemIcon>
          <ListItemText
            primary={item.title}
            sx={{
              opacity: isExpanded ? 1 : 0,
              transition: 'opacity 0.2s',
              whiteSpace: 'nowrap',
            }}
          />
        </ListItemButton>
      </Tooltip>
    </ListItem>
  );

  const sidebarContent = (
    <>
      <DrawerHeader>
        <Box sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'primary.main',
          overflow: 'hidden',
        }}>
          {isExpanded ? (
            <img src={AionaLogo} alt="AIONA" style={{ height: 32 }} />
          ) : (
            <Box sx={{ color: 'white', fontWeight: 700, fontSize: 24 }}>A</Box>
          )}
        </Box>
      </DrawerHeader>
      <Divider />
      <List>{navigationItems.map(renderNavItem)}</List>
      <Divider />
      <List>{actionItems.map(renderNavItem)}</List>
      <Box sx={{ flexGrow: 1 }} />
      <Divider />
      <List>{bottomItems.map(renderNavItem)}</List>
    </>
  );

  // Mobile: temporary drawer
  if (isMobile) {
    if (sidebarMode === 'hidden') return null;
    return (
      <Drawer
        variant="temporary"
        open
        onClose={onCloseMobile}
        sx={{
          '& .MuiDrawer-paper': {
            width: EXPANDED_WIDTH,
            boxSizing: 'border-box',
          },
        }}
      >
        {sidebarContent}
      </Drawer>
    );
  }

  // Desktop: fixed sidebar with hover expand overlay
  return (
    <>
      {/* Spacer: reserves layout space matching sidebar mode */}
      <Box sx={{
        width: sidebarMode === 'expanded' ? EXPANDED_WIDTH : MINI_WIDTH,
        flexShrink: 0,
        transition: (theme) => theme.transitions.create('width', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
      }} />

      {/* Fixed sidebar panel */}
      <Box
        onMouseEnter={() => isMini && setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        sx={{
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
          width: currentWidth,
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.paper',
          borderRight: '1px solid',
          borderColor: 'divider',
          overflowX: 'hidden',
          zIndex: Z_INDEX.SIDEBAR,
          transition: (theme) =>
            theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
          boxShadow: (isMini && hovered) ? 4 : 0,
        }}
      >
        {sidebarContent}
      </Box>
    </>
  );
};

export default Sidebar;
